/**
 * Create Curriculum Dialog Component
 * Form for creating new curriculum with learning goals and file uploads
 */

import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
    Plus,
    Sparkles,
    Upload,
    FileText,
    Loader2,
    Trash2
} from 'lucide-react';

const CreateCurriculumDialog = ({
    open,
    onOpenChange,
    onSubmit,
    creating,
    uploadedFiles,
    setUploadedFiles,
    uploading
}) => {
    const [form, setForm] = useState({
        title: '',
        description: '',
        target_audience: '',
        difficulty_progression: 'beginner_to_advanced',
        learning_goals: [''],
        estimated_duration: '4 weeks',
        additional_instructions: ''
    });

    const addLearningGoal = () => {
        setForm(prev => ({
            ...prev,
            learning_goals: [...prev.learning_goals, '']
        }));
    };

    const updateLearningGoal = (index, value) => {
        const goals = [...form.learning_goals];
        goals[index] = value;
        setForm(prev => ({ ...prev, learning_goals: goals }));
    };

    const removeLearningGoal = (index) => {
        if (form.learning_goals.length === 1) return;
        const goals = form.learning_goals.filter((_, i) => i !== index);
        setForm(prev => ({ ...prev, learning_goals: goals }));
    };

    const removeUploadedFile = (fileId) => {
        setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const handleSubmit = () => {
        onSubmit(form);
        // Reset form after submission
        setForm({
            title: '',
            description: '',
            target_audience: '',
            difficulty_progression: 'beginner_to_advanced',
            learning_goals: [''],
            estimated_duration: '4 weeks',
            additional_instructions: ''
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-yellow-500" />
                        Create New Curriculum
                    </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                        <Label>Curriculum Title *</Label>
                        <Input
                            value={form.title}
                            onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="e.g., Mastering Negotiation Skills"
                            data-testid="curriculum-title-input"
                        />
                    </div>
                    
                    <div className="space-y-2">
                        <Label>Description *</Label>
                        <Textarea
                            value={form.description}
                            onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Describe what this curriculum will cover, the key topics, and expected outcomes..."
                            rows={3}
                        />
                    </div>
                    
                    <div className="space-y-2">
                        <Label>Target Audience *</Label>
                        <Input
                            value={form.target_audience}
                            onChange={(e) => setForm(prev => ({ ...prev, target_audience: e.target.value }))}
                            placeholder="e.g., Sales professionals, HR managers, New graduates"
                        />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Difficulty Progression</Label>
                            <Select
                                value={form.difficulty_progression}
                                onValueChange={(v) => setForm(prev => ({ ...prev, difficulty_progression: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="beginner_to_advanced">Beginner → Advanced</SelectItem>
                                    <SelectItem value="intermediate">Intermediate Only</SelectItem>
                                    <SelectItem value="advanced_only">Advanced Only</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Estimated Duration</Label>
                            <Select
                                value={form.estimated_duration}
                                onValueChange={(v) => setForm(prev => ({ ...prev, estimated_duration: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="1 week">1 Week</SelectItem>
                                    <SelectItem value="2 weeks">2 Weeks</SelectItem>
                                    <SelectItem value="4 weeks">4 Weeks</SelectItem>
                                    <SelectItem value="8 weeks">8 Weeks</SelectItem>
                                    <SelectItem value="12 weeks">12 Weeks</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    
                    <div className="space-y-2">
                        <Label>Learning Goals</Label>
                        {form.learning_goals.map((goal, idx) => (
                            <div key={idx} className="flex gap-2">
                                <Input
                                    value={goal}
                                    onChange={(e) => updateLearningGoal(idx, e.target.value)}
                                    placeholder={`Goal ${idx + 1}: e.g., Understand negotiation fundamentals`}
                                />
                                {form.learning_goals.length > 1 && (
                                    <Button 
                                        variant="ghost" 
                                        size="icon"
                                        onClick={() => removeLearningGoal(idx)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={addLearningGoal}>
                            <Plus className="h-4 w-4 mr-1" />
                            Add Goal
                        </Button>
                    </div>
                    
                    <div className="space-y-2">
                        <Label>Additional Instructions (Optional)</Label>
                        <Textarea
                            value={form.additional_instructions}
                            onChange={(e) => setForm(prev => ({ ...prev, additional_instructions: e.target.value }))}
                            placeholder="Any specific requirements, focus areas, or constraints for the AI..."
                            rows={2}
                        />
                    </div>
                    
                    {/* File Upload Section */}
                    <div className="space-y-3 p-4 rounded-lg bg-muted/50 border border-dashed">
                        <Label className="flex items-center gap-2">
                            <Upload className="h-4 w-4" />
                            Reference Documents (Optional)
                        </Label>
                        <p className="text-xs text-muted-foreground">
                            Upload PDFs, DOCX, or TXT files to help the AI generate more relevant content
                        </p>
                        
                        <input
                            type="file"
                            id="reference-upload"
                            multiple
                            accept=".pdf,.docx,.txt,.md"
                            className="hidden"
                            onChange={(e) => {
                                const files = Array.from(e.target.files);
                                const previews = files.map(f => ({
                                    id: `preview-${Date.now()}-${Math.random()}`,
                                    filename: f.name,
                                    file: f,
                                    preview: true
                                }));
                                setUploadedFiles(prev => [...prev, ...previews]);
                            }}
                            data-testid="reference-file-input"
                        />
                        <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full gap-2"
                            onClick={() => document.getElementById('reference-upload').click()}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Upload className="h-4 w-4" />
                            )}
                            Select Files
                        </Button>
                        
                        {uploadedFiles.length > 0 && (
                            <div className="space-y-2 mt-2">
                                {uploadedFiles.map(file => (
                                    <div key={file.id} className="flex items-center justify-between p-2 rounded-lg bg-background border">
                                        <div className="flex items-center gap-2">
                                            <FileText className="h-4 w-4 text-primary" />
                                            <span className="text-sm truncate max-w-[180px]">{file.filename}</span>
                                            {file.wordCount && (
                                                <Badge variant="secondary" className="text-xs">
                                                    {file.wordCount} words
                                                </Badge>
                                            )}
                                            {file.preview && (
                                                <Badge variant="outline" className="text-xs">
                                                    Pending
                                                </Badge>
                                            )}
                                        </div>
                                        <Button 
                                            variant="ghost" 
                                            size="icon"
                                            className="h-6 w-6"
                                            onClick={() => removeUploadedFile(file.id)}
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    <Button 
                        onClick={handleSubmit} 
                        disabled={creating || !form.title || !form.description || !form.target_audience} 
                        className="w-full" 
                        data-testid="create-curriculum-btn"
                    >
                        {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                        Create Curriculum
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CreateCurriculumDialog;
